import { useMemo } from "react"
import * as Icons from "@nova/component-icons"
import { ButtonDestructive, ButtonTertiary } from "@nova/component-btn"
import { ScrollableArea } from "@nova/component-scrollable-area"
import { FileExtension, Permissions } from "@nova/constants"
import { config } from "@nova/configuration/browser"
import { useTranslation } from "@nova/use-translation"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { useRouter } from "pages"
import { selectActivatedFireId } from "features/map/control/selectors"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import { UploadDropBox } from "panels/content/side-panel/upload-flow/components/UploadDropBox"
import {
    FileUploadCategory,
    mapExtensionsToMimeType,
    supportedFileUploadExtensions,
} from "features/job-management/uploader/constants"
import { selectData, setData } from "plugins/PDFv2/PDFSlice"
import { hasCustomWorkspaceLogo, parsePdfLogoSettings } from "plugins/shared/pdfLogos"

export const PDFLogos = () => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const projectId = useAppSelector(selectActivatedFireId)
    // Project-scoped permission: project editors can change this PDF's logos.
    const { isEditor: isProjectEditor, members, selfId } = useWorkspacePermissions(projectId)
    const router = useRouter("/workspace/:workspaceId")
    const leftLogo = useAppSelector(selectData("leftLogo"))
    const rightLogo = useAppSelector(selectData("rightLogo"))
    const { pdfLogos } = config.use("pdfLogos")

    const isWorkspaceEditor = useMemo(() => {
        const access = members?.find((member) => member.userId === selfId)?.permissions
            .workspaceAccess
        return access === Permissions.Editor || access === Permissions.Admin
    }, [members, selfId])

    const showDefaultsCTA = useMemo(() => {
        if (!isWorkspaceEditor) return false
        return !hasCustomWorkspaceLogo(parsePdfLogoSettings(pdfLogos))
    }, [isWorkspaceEditor, pdfLogos])

    // Uploading here only sets the logo for this PDF (a local blob URL); it does
    // not change the workspace default logo setting.
    const handleLogo = (slot: "leftLogo" | "rightLogo") => (files: File[]) => {
        const file = files[0]
        if (file) {
            dispatch(setData({ type: slot, data: URL.createObjectURL(file) }))
        }
        return Promise.resolve(true)
    }

    const clearLogo = (slot: "leftLogo" | "rightLogo") => {
        dispatch(setData({ type: slot, data: "" }))
    }

    const uploadBox = (slot: "leftLogo" | "rightLogo") => (
        <UploadDropBox
            allowedFileTypes={[FileExtension.png, FileExtension.jpg, FileExtension.jpeg]}
            title="Upload Images"
            onNewItems={handleLogo(slot)}
            isProcessing={false}
            supportedMimeTypes={supportedFileUploadExtensions[FileUploadCategory.Logo].map(
                (extension) => mapExtensionsToMimeType[extension]
            )}
        />
    )

    return (
        <ScrollableArea
            className="flex flex-col gap-[8px] w-full pr-2"
            style={{ maxHeight: "calc(100vh - 150px)" }}
        >
            <small>{t("Left Logo")}</small>
            {leftLogo ? (
                <>
                    <div className="flex justify-center bg-white dark:bg-gray-900 py-6 px-2 rounded-lg">
                        <img
                            src={leftLogo}
                            alt="left-logo"
                            width={160}
                            height={40}
                            className="max-h-[80px] w-auto object-contain"
                        />
                    </div>
                    {isProjectEditor && (
                        <ButtonDestructive
                            LeftIcon={Icons.Trash}
                            onClick={() => clearLogo("leftLogo")}
                        />
                    )}
                </>
            ) : (
                isProjectEditor && uploadBox("leftLogo")
            )}
            <small>{t("Right Logo")}</small>
            {rightLogo ? (
                <>
                    <div className="flex justify-center bg-white dark:bg-gray-900  py-6 px-2 rounded-lg">
                        <img
                            src={rightLogo}
                            alt="right-logo"
                            width={220}
                            height={130}
                            className="max-h-[130px] w-auto object-contain"
                        />
                    </div>
                    {isProjectEditor && (
                        <ButtonDestructive
                            LeftIcon={Icons.Trash}
                            onClick={() => clearLogo("rightLogo")}
                        />
                    )}
                </>
            ) : (
                isProjectEditor && uploadBox("rightLogo")
            )}
            {showDefaultsCTA && (
                <>
                    <div className="border-b border-gray-700 my-4" />
                    <small className="text-gray-300">
                        {t("Set up default logos for every PDF in your workspace")}
                    </small>
                    <ButtonTertiary
                        title="Go to Settings"
                        RightIcon={Icons.ArrowRight}
                        onClick={() => router.navigate({ path: "/settings/configuration" })}
                    />
                </>
            )}
        </ScrollableArea>
    )
}
